import React, { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

const styles = require('./Tooltip.module.scss');

const CLOSE_DELAY_MS = 150;
const VIEWPORT_MARGIN = 16;
const GAP = 8;

const FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

let tooltipIdCounter = 0;

const DefaultInfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 19 19" fill="none" aria-hidden="true">
    <path
      d="M9.03335 12.3667V9.03335M9.03335 5.70001H9.04168M17.3667 9.03335C17.3667 13.6357 13.6357 17.3667 9.03335 17.3667C4.43097 17.3667 0.700012 13.6357 0.700012 9.03335C0.700012 4.43097 4.43097 0.700012 9.03335 0.700012C13.6357 0.700012 17.3667 4.43097 17.3667 9.03335Z"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

type TooltipProps = {
  children: ReactNode;
  triggerAriaLabel: string;
  triggerIcon?: ReactNode;
  role?: 'tooltip' | 'dialog';
  wrapperClassName?: string;
  popoverClassName?: string;
  stopPropagation?: boolean;
  trapFocus?: boolean;
};

export default function Tooltip({
  children,
  triggerAriaLabel,
  triggerIcon,
  role = 'tooltip',
  wrapperClassName,
  popoverClassName,
  stopPropagation = false,
  trapFocus = false,
}: TooltipProps) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const [id] = useState(() => `sg-tooltip-${++tooltipIdCounter}`);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const popoverId = `${id}-popover`;

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const show = useCallback(() => {
    clearCloseTimer();
    setOpen(true);
  }, []);

  const hide = useCallback(() => {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  }, []);

  useEffect(() => () => clearCloseTimer(), []);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }

    const reposition = () => {
      if (!triggerRef.current || !popoverRef.current) return;
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const popoverRect = popoverRef.current.getBoundingClientRect();

      const idealLeft = triggerRect.left + triggerRect.width / 2 - popoverRect.width / 2;
      const maxLeft = window.innerWidth - popoverRect.width - VIEWPORT_MARGIN;
      const left = Math.max(VIEWPORT_MARGIN, Math.min(idealLeft, maxLeft));

      const aboveTop = triggerRect.top - GAP - popoverRect.height;
      const belowTop = triggerRect.bottom + GAP;
      const top = aboveTop >= VIEWPORT_MARGIN ? aboveTop : belowTop;

      setPosition({ top, left });
    };

    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const handleWrapperClick = (event: React.MouseEvent) => {
    if (stopPropagation) event.stopPropagation();
  };

  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (stopPropagation && (event.key === ' ' || event.key === 'Enter')) {
      event.stopPropagation();
    }
    if (trapFocus && event.key === 'Tab' && !event.shiftKey && open && popoverRef.current) {
      const firstFocusable = popoverRef.current.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      if (firstFocusable) {
        event.preventDefault();
        firstFocusable.focus();
      }
    }
  };

  const handlePopoverKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (trapFocus && event.key === 'Tab' && event.shiftKey) {
      event.preventDefault();
      triggerRef.current?.focus();
    }
  };

  const wrapperClasses = wrapperClassName ? `${styles.wrapper} ${wrapperClassName}` : styles.wrapper;
  const popoverClasses = popoverClassName ? `${styles.popover} ${popoverClassName}` : styles.popover;
  const icon = triggerIcon ?? <DefaultInfoIcon />;

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <span className={wrapperClasses} onClick={stopPropagation ? handleWrapperClick : undefined}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        aria-label={triggerAriaLabel}
        aria-haspopup={role === 'dialog' ? 'dialog' : undefined}
        aria-expanded={role === 'dialog' ? open : undefined}
        aria-describedby={role === 'tooltip' ? popoverId : undefined}
        aria-controls={popoverId}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onKeyDown={handleTriggerKeyDown}
      >
        {icon}
      </button>
      {open && ReactDOM.createPortal(
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <div
          ref={popoverRef}
          id={popoverId}
          className={popoverClasses}
          style={{
            top: position?.top ?? 0,
            left: position?.left ?? 0,
            visibility: position ? 'visible' : 'hidden',
          }}
          role={role}
          onMouseEnter={show}
          onMouseLeave={hide}
          onFocus={trapFocus ? show : undefined}
          onBlur={trapFocus ? hide : undefined}
          onKeyDown={handlePopoverKeyDown}
        >
          {children}
        </div>,
        document.body,
      )}
    </span>
  );
}
